import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Checkbox,
  Flex,
  Text,
  Input,
  Textarea,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Box,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { ChevronDown, Search } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useMemo, useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { iconSize, menuMaxHeight } from '@duke-hq/ui/constants/scalable';
import { CourseStatusSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseStatus.schema';
import { PaginatedCourse, Course, SupersedeFormData } from './types';
import { djs } from '@duke-hq/djs';

type Option = {
  label: string;
  value: string;
  code: string | null;
  createdAt: Date | null;
};

export function SelectReplacementCourse({
  currentCourse,
  allCourses,
  goToNextStep,
  setReplacementCourse,
  setFormData,
  initialFormData,
  title,
  isEffectiveDatePassed,
  isTeachOutDatePassed,
}: {
  currentCourse: PaginatedCourse;
  allCourses: Course[];
  goToNextStep: () => void;
  setReplacementCourse: (course: Course) => void;
  setFormData: (data: SupersedeFormData) => void;
  initialFormData?: SupersedeFormData;
  handleSubmit?: () => void;
  title: string;
  isEffectiveDatePassed: boolean;
  isTeachOutDatePassed: boolean;
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const formSchema = z
    .object({
      supersededByCourse: z
        .object({
          label: z.string(),
          value: z.string(),
          code: z.string().nullable(),
          createdAt: z.date().nullable(),
        })
        .nullable()
        .refine((val) => val !== null, {
          message: t`Please select a superseding course`,
        }),
      supersededEffectiveDate: z.string().min(1, t`Effective date is required`),
      teachOutDate: z.string().min(1, t`Teach-out date is required`),
      notes: z.string().optional(),
    })
    .refine(
      (data) => {
        if (!data.supersededEffectiveDate || !data.teachOutDate) {
          return true;
        }
        return new Date(data.supersededEffectiveDate) <= new Date(data.teachOutDate);
      },
      {
        message: t`Effective date must be on or before the teach-out date`,
        path: ['teachOutDate'],
      },
    );

  type FormData = z.infer<typeof formSchema>;

  const initialSupersedingCourseOption = useMemo<Option | null>(() => {
    if (initialFormData?.supersededByCourse) {
      return {
        label: initialFormData.supersededByCourse.title,
        value: initialFormData.supersededByCourse.id,
        code: initialFormData.supersededByCourse.identifier,
        createdAt: new Date(initialFormData.supersededByCourse.created_at),
      };
    }
    return null;
  }, [initialFormData?.supersededByCourse]);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      supersededByCourse: null,
      supersededEffectiveDate: '',
      teachOutDate: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (initialFormData || initialSupersedingCourseOption) {
      reset({
        supersededByCourse: initialSupersedingCourseOption,
        supersededEffectiveDate: initialFormData?.supersededEffectiveDate ?? '',
        teachOutDate: initialFormData?.teachOutDate ?? '',
        notes: initialFormData?.notes ?? '',
      });
    }
  }, [initialFormData, initialSupersedingCourseOption, reset]);

  const courseOptions = useMemo<Option[]>(() => {
    return allCourses
      .filter((course) => course.id !== currentCourse.id)
      .map((course) => ({
        label: course.title,
        value: course.id,
        code: course.identifier,
        createdAt: new Date(course.created_at),
      }));
  }, [allCourses, currentCourse.id]);

  const filteredCourseOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return courseOptions;
    }

    const query = searchQuery.toLowerCase();
    return courseOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(query) ||
        (option.code != null && option.code.toLowerCase().includes(query)),
    );
  }, [courseOptions, searchQuery]);

  const onSubmit = (data: FormData) => {
    if (data.supersededByCourse) {
      const selectedCourse = allCourses.find(
        (course) => course.id === data.supersededByCourse?.value,
      );
      if (selectedCourse) {
        setReplacementCourse(selectedCourse);
        setFormData({
          supersededEffectiveDate: data.supersededEffectiveDate,
          teachOutDate: data.teachOutDate,
          notes: data.notes,
          supersededByCourse: selectedCourse,
        });
      }
    }
    goToNextStep();
  };

  const isSupersededByDisabled = isEffectiveDatePassed || isTeachOutDatePassed;

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} w="full" noValidate>
      <Text fontSize="lg" fontWeight="semibold" mb={4}>
        {title}
      </Text>

      <VStack spacing={4} align="stretch">
        <FormControl isRequired isInvalid={!!errors.supersededByCourse}>
          <FormLabel htmlFor="supersededByCourse">
            <Trans>Superseded by</Trans>
          </FormLabel>
          <Controller
            name="supersededByCourse"
            control={control}
            render={({ field }) => (
              <Menu closeOnSelect={true} matchWidth={true}>
                {({ onClose: closeMenu }) => (
                  <>
                    <MenuButton
                      as={Button}
                      variant="outline"
                      colorScheme="gray"
                      width="100%"
                      textAlign="left"
                      rightIcon={
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            field.onChange(null);
                            setSearchQuery('');
                          }}
                        >
                          <ChevronDown size={iconSize.md} />
                        </Box>
                      }
                      fontWeight="normal"
                      isDisabled={isSupersededByDisabled}
                      opacity={isSupersededByDisabled ? 0.6 : 1}
                      cursor={isSupersededByDisabled ? 'not-allowed' : 'pointer'}
                      _disabled={{
                        opacity: 0.6,
                        cursor: 'not-allowed',
                        bg: 'gray.100',
                      }}
                    >
                      {field.value !== null ? (
                        <Flex align="center" gap={2}>
                          {field.value.code != null && field.value.code !== '' ? (
                            <Text fontWeight="medium" color="fg.subtle">
                              {field.value.code}
                            </Text>
                          ) : null}
                          <Text fontWeight="normal" color="fg.default">
                            {field.value.label}
                          </Text>
                          <Text fontWeight="normal" color="fg.subtle">
                            {field.value.createdAt !== null
                              ? djs(field.value.createdAt).format('DD MMM YYYY')
                              : ''}
                          </Text>
                        </Flex>
                      ) : (
                        <Text color="gray.500">{t`Choose course`}</Text>
                      )}
                    </MenuButton>
                    <MenuList maxH={menuMaxHeight} overflowY="auto">
                      <Box px={3} pb={2}>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <Search size={iconSize.md} color="gray" />
                          </InputLeftElement>
                          <Input
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </InputGroup>
                      </Box>
                      {filteredCourseOptions.length > 0 ? (
                        filteredCourseOptions.map((option) => (
                          <MenuItem
                            key={option.value}
                            onClick={() => {
                              field.onChange(option);
                              setSearchQuery('');
                              closeMenu();
                            }}
                          >
                            <Flex align="center" gap={3} w="100%">
                              <Checkbox
                                isChecked={field.value?.value === option.value}
                                pointerEvents="none"
                                flexShrink={0}
                              />
                              <Flex align="center" gap={2} flex={1} overflow="hidden">
                                {option.code != null && option.code !== '' ? (
                                  <Text fontWeight="medium" color="gray.600" flexShrink={0}>
                                    {option.code}
                                  </Text>
                                ) : null}
                                <Text noOfLines={1} flex={1}>
                                  {option.label}
                                </Text>
                                <Text fontSize="sm" color="gray.500" flexShrink={0}>
                                  {option.createdAt !== null
                                    ? djs(option.createdAt).format('DD MMM YYYY')
                                    : ''}
                                </Text>
                              </Flex>
                            </Flex>
                          </MenuItem>
                        ))
                      ) : (
                        <Box px={3} py={2}>
                          <Text color="gray.500" fontSize="sm">
                            <Trans>No courses found</Trans>
                          </Text>
                        </Box>
                      )}
                    </MenuList>
                  </>
                )}
              </Menu>
            )}
          />
          <FormErrorMessage>{errors.supersededByCourse?.message}</FormErrorMessage>
        </FormControl>

        <HStack spacing={4} alignItems="start">
          <FormControl isRequired isInvalid={!!errors.supersededEffectiveDate}>
            <FormLabel htmlFor="effective_date">
              <Trans>Effective date</Trans>
            </FormLabel>
            <Input
              id="effective_date"
              type="date"
              {...register('supersededEffectiveDate')}
              isDisabled={isEffectiveDatePassed}
              opacity={isEffectiveDatePassed ? 0.6 : 1}
              cursor={isEffectiveDatePassed ? 'not-allowed' : 'default'}
              _disabled={{
                opacity: 0.6,
                cursor: 'not-allowed',
                bg: 'gray.100',
              }}
            />
            <FormErrorMessage minH="20px">
              {errors.supersededEffectiveDate?.message}
            </FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.teachOutDate}>
            <FormLabel htmlFor="teach_out_date">
              <Trans>Teach-out date</Trans>
            </FormLabel>
            <Input
              id="teach_out_date"
              type="date"
              {...register('teachOutDate')}
              isDisabled={isTeachOutDatePassed}
              opacity={isTeachOutDatePassed ? 0.6 : 1}
              cursor={isTeachOutDatePassed ? 'not-allowed' : 'default'}
              _disabled={{
                opacity: 0.6,
                cursor: 'not-allowed',
                bg: 'gray.100',
              }}
            />
            <FormErrorMessage minH="20px">{errors.teachOutDate?.message}</FormErrorMessage>
          </FormControl>
        </HStack>

        <FormControl>
          <FormLabel htmlFor="notes">
            <Trans>Notes</Trans>
          </FormLabel>
          <Textarea
            id="notes"
            placeholder={t`Add notes`}
            rows={4}
            {...register('notes')}
            isDisabled={isEffectiveDatePassed && isTeachOutDatePassed}
            opacity={isEffectiveDatePassed && isTeachOutDatePassed ? 0.6 : 1}
            cursor={isEffectiveDatePassed && isTeachOutDatePassed ? 'not-allowed' : 'default'}
            _disabled={{
              opacity: 0.6,
              cursor: 'not-allowed',
              bg: 'gray.100',
            }}
          />
        </FormControl>

        <Alert status="warning">
          <HStack>
            <AlertIcon />
          </HStack>
          <AlertDescription>
            <Text fontSize="sm">
              {currentCourse.status === CourseStatusSchema.enum.PENDING_SUPERSESSION ? (
                isEffectiveDatePassed && isTeachOutDatePassed ? (
                  <Trans>
                    The effective date and teach-out date have passed. No changes can be made to
                    this supersession.
                  </Trans>
                ) : (
                  <Trans>
                    Changing these dates may cause students transferred at different times to be
                    moved to different courses based on the new dates. This action is final and
                    cannot be undone.
                  </Trans>
                )
              ) : (
                <Trans>
                  This action cannot be undone. Once the effective date is reached, this course will
                  be marked as superseded. Existing enrolments will transfer to the replacement
                  course on the teach-out date.
                </Trans>
              )}
            </Text>
          </AlertDescription>
        </Alert>
      </VStack>

      {!(isEffectiveDatePassed && isTeachOutDatePassed) ? (
        <Flex justify="flex-end" mt={4}>
          <Button colorScheme="brand" type="submit">
            <Trans>Next</Trans>
          </Button>
        </Flex>
      ) : null}
    </Box>
  );
}
